
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { parseFlightCSV } from '@/utils/csvParser';
import { createFlight } from '@/services/flightService';

export interface UploadResult {
  success: boolean;
  totalFlights: number;
  successCount: number;
  errorCount: number;
  errors: Array<{ flightNumber: string; row: number; reason: string }>;
  uploadedFlights: string[];
}

export function useFlightUpload() {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const uploadFlights = async (file: File): Promise<UploadResult> => {
    if (!file) {
      toast({
        variant: "destructive",
        title: "No file selected",
        description: "Please select a CSV file to upload.",
      });
      return {
        success: false,
        totalFlights: 0,
        successCount: 0,
        errorCount: 0,
        errors: [],
        uploadedFlights: [],
      };
    }

    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Authentication required",
        description: "You must be logged in to upload flights.",
      });
      return {
        success: false,
        totalFlights: 0,
        successCount: 0,
        errorCount: 0,
        errors: [],
        uploadedFlights: [],
      };
    }

    setUploading(true);
    try {
      console.log('Starting flight upload process...');
      const csvText = await file.text();
      console.log('CSV file contents loaded, parsing...');
      
      const flights = await parseFlightCSV(csvText);
      console.log(`Successfully parsed ${flights.length} flights from CSV`);
      console.log('Sample flight data:', flights[0]);
      
      // Upload each flight with detailed progress logging
      let successCount = 0;
      let errorCount = 0;
      const errors: Array<{ flightNumber: string; row: number; reason: string }> = [];
      const uploadedFlights: string[] = [];

      console.log('Beginning flight upload to database...');
      
      for (let i = 0; i < flights.length; i++) {
        try {
          const flightData = {
            flight_number: flights[i].flight_number,
            organization_id: flights[i].organization_id,
            departure_city_id: flights[i].departure_city_id,
            arrival_city_id: flights[i].arrival_city_id,
            aircraft_type_id: flights[i].aircraft_type_id,
            scheduled_departure: flights[i].scheduled_departure,
            scheduled_arrival: flights[i].scheduled_arrival,
            status: flights[i].status,
            status_reason: flights[i].status_reason,
            gate: flights[i].gate,
            created_by: user.id,
            updated_by: user.id,
          };
          
          console.log(`Uploading flight ${i + 1}/${flights.length}: ${flights[i].flight_number}`);
          await createFlight(flightData);
          
          successCount++;
          uploadedFlights.push(flights[i].flight_number);
          console.log(`✅ Successfully uploaded flight ${flights[i].flight_number}`);
        } catch (error: any) {
          errorCount++;
          errors.push({
            flightNumber: flights[i].flight_number,
            row: i + 2, // +2 because of header row and 0-based index
            reason: error.message,
          });
          console.error(`❌ Error uploading flight ${flights[i].flight_number}:`, error);
        }
      }

      console.log(`Upload completed: ${successCount} successful, ${errorCount} failed`);
      console.log('Uploaded flights:', uploadedFlights);

      if (successCount > 0 && errorCount === 0) {
        toast({
          title: "Upload successful",
          description: `Successfully uploaded ${successCount} flights.`,
        });
        console.log('🎉 All flights uploaded successfully!');
      } else if (successCount > 0 && errorCount > 0) {
        toast({
          title: "Partial upload",
          description: `Uploaded ${successCount} flights. ${errorCount} failed. See details below.`,
        });
        console.error('Upload errors:', errors);
      } else {
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: `All ${errorCount} flights failed to upload. See details below.`,
        });
        console.error('Upload errors:', errors);
      }
      
      return {
        success: successCount > 0,
        totalFlights: flights.length,
        successCount,
        errorCount,
        errors,
        uploadedFlights,
      };
      
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: error.message || "Failed to parse or upload flights.",
      });
      console.error('Upload error:', error);
      return {
        success: false,
        totalFlights: 0,
        successCount: 0,
        errorCount: 0,
        errors: [],
        uploadedFlights: [],
      };
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    uploadFlights,
  };
}
